import type { EmodeMarketCategory, Market, MarketUserState, Reserve } from '@aave/graphql';
import { UserReserveData } from '@aave/math-utils';
import { client } from 'pages/_app.page';
import React, { PropsWithChildren, useContext } from 'react';
import { EmodeCategory } from 'src/helpers/types';
import { useWeb3Context } from 'src/libs/hooks/useWeb3Context';
import { useRootStore } from 'src/store/root';
import { CustomMarket } from 'src/ui-config/marketsConfig';

import { formatEmodes } from '../../store/poolSelectors';
import {
  ExtendedFormattedUser as _ExtendedFormattedUser,
  useExtendedUserSummaryAndIncentives,
} from '../pool/useExtendedUserSummaryAndIncentives';
import {
  FormattedReservesAndIncentives,
  usePoolFormattedReserves,
} from '../pool/usePoolFormattedReserves';
import { usePoolReservesHumanized } from '../pool/usePoolReserves';
import { useUserPoolReservesHumanized } from '../pool/useUserPoolReserves';
import { FormattedUserReserves } from '../pool/useUserSummaryAndIncentives';
import { useMarketsData } from './useMarketsData';

/**
 * removes the marketPrefix from a symbol
 * @param symbol
 * @param prefix
 */
export const unPrefixSymbol = (symbol: string, prefix: string) => {
  return symbol.toUpperCase().replace(RegExp(`^(${prefix[0]}?${prefix.slice(1)})`), '');
};

/**
 * @deprecated Use FormattedReservesAndIncentives type from usePoolFormattedReserves hook
 */
export type ComputedReserveData = FormattedReservesAndIncentives;

/**
 * @deprecated Use FormattedUserReserves type from useUserSummaryAndIncentives hook
 */
export type ComputedUserReserveData = FormattedUserReserves;

/**
 * @deprecated Use ExtendedFormattedUser type from useExtendedUserSummaryAndIncentives hook
 */
export type ExtendedFormattedUser = _ExtendedFormattedUser;
export type ReserveWithId = Reserve & { id: string };
export interface AppDataContextType {
  loading: boolean;
  /** SDK market snapshot */
  market?: Market;
  totalBorrows?: number;
  supplyReserves: ReserveWithId[];
  borrowReserves: ReserveWithId[];
  eModeCategories: EmodeMarketCategory[];
  userState?: MarketUserState;
  /** Legacy fields (deprecated) kept temporarily for incremental migration */
  reserves: ComputedReserveData[];
  eModes: Record<number, EmodeCategory>;
  user?: ExtendedFormattedUser;
  marketReferencePriceInUsd: string;
  marketReferenceCurrencyDecimals: number;
  userReserves: UserReserveData[];
}

const AppDataContext = React.createContext<AppDataContextType>({} as AppDataContextType);

const makeDecimalValue = (value: string, decimals: number, raw = value) => ({
  __typename: 'DecimalValue' as const,
  raw,
  decimals,
  value,
});

const makePercentValue = (value: string, raw = value) => ({
  __typename: 'PercentValue' as const,
  raw,
  decimals: 27,
  value,
  formatted: value,
});

const makeTokenAmount = (
  value: string,
  raw: string,
  decimals: number,
  usdPerToken: string,
  usd: string
) => ({
  __typename: 'TokenAmount' as const,
  usdPerToken,
  amount: makeDecimalValue(value, decimals, raw),
  usd,
});

const buildDinolendMarket = (
  marketData: ReturnType<typeof useRootStore.getState>['currentMarketData'],
  formattedPoolReserves: FormattedReservesAndIncentives[] | undefined
) => {
  if (!formattedPoolReserves) return undefined;

  const marketInfo = {
    __typename: 'MarketInfo' as const,
    name: marketData.marketTitle,
    chain: {
      __typename: 'Chain' as const,
      name: marketData.marketTitle,
      icon: marketData.logo,
      chainId: marketData.chainId,
      explorerUrl: '',
      isTestnet: true,
      nativeWrappedToken:
        formattedPoolReserves.find((reserve) => reserve.isWrappedBaseAsset)?.underlyingAsset ||
        '0x0000000000000000000000000000000000000000',
    },
    address: marketData.addresses.LENDING_POOL,
    icon: marketData.logo,
  };

  const toReserve = (reserve: FormattedReservesAndIncentives) => {
    const token = {
      __typename: 'Currency' as const,
      address: reserve.underlyingAsset,
      imageUrl: reserve.iconSymbol || reserve.symbol.toLowerCase(),
      name: reserve.name,
      symbol: reserve.symbol,
      decimals: reserve.decimals,
      chainId: marketData.chainId,
    };
    const tokenAmount = (value: string, raw: string, usd: string) =>
      makeTokenAmount(value, raw, reserve.decimals, reserve.priceInUSD, usd);
    const isBorrowEnabled = reserve.borrowingEnabled && !reserve.isFrozen && !reserve.isPaused;

    return {
      __typename: 'Reserve' as const,
      id: `${marketData.addresses.LENDING_POOL}-${reserve.underlyingAsset}`,
      market: marketInfo,
      underlyingToken: token,
      aToken: {
        ...token,
        address: reserve.aTokenAddress,
        name: `Dinolend ${reserve.symbol}`,
        symbol: `d${reserve.symbol}`,
      },
      vToken: {
        ...token,
        address: reserve.variableDebtTokenAddress,
        name: `Dinolend variable debt ${reserve.symbol}`,
        symbol: `variableDebt${reserve.symbol}`,
      },
      acceptsNative: reserve.isWrappedBaseAsset
        ? {
            __typename: 'NativeCurrency' as const,
            imageUrl: 'eth',
            name: 'Ether',
            symbol: 'ETH',
            decimals: 18,
            chainId: marketData.chainId,
            wrappedToken: reserve.underlyingAsset,
          }
        : null,
      size: tokenAmount(
        reserve.totalLiquidity,
        reserve.availableLiquidity,
        reserve.totalLiquidityUSD
      ),
      usdExchangeRate: reserve.priceInUSD,
      usdOracleAddress: reserve.priceOracle,
      isFrozen: reserve.isFrozen,
      isPaused: reserve.isPaused,
      flashLoanEnabled: reserve.flashLoanEnabled,
      interestRateStrategyAddress: reserve.interestRateStrategyAddress,
      permitSupported: false,
      supplyInfo: {
        __typename: 'ReserveSupplyInfo' as const,
        apy: makePercentValue(reserve.supplyAPY, reserve.liquidityRate),
        total: makeDecimalValue(
          reserve.totalLiquidity,
          reserve.decimals,
          reserve.availableLiquidity
        ),
        maxLTV: makePercentValue(reserve.formattedBaseLTVasCollateral, reserve.baseLTVasCollateral),
        liquidationThreshold: makePercentValue(
          reserve.formattedReserveLiquidationThreshold,
          reserve.reserveLiquidationThreshold
        ),
        liquidationBonus: makePercentValue(
          reserve.formattedReserveLiquidationBonus,
          reserve.reserveLiquidationBonus
        ),
        canBeCollateral: reserve.usageAsCollateralEnabled,
        supplyCap: tokenAmount(reserve.supplyCap, reserve.supplyCap, reserve.supplyCapUSD),
        supplyCapReached:
          reserve.supplyCap !== '0' && Number(reserve.totalLiquidity) >= Number(reserve.supplyCap),
      },
      borrowInfo: {
        __typename: 'ReserveBorrowInfo' as const,
        apy: makePercentValue(reserve.variableBorrowAPY, reserve.variableBorrowRate),
        total: tokenAmount(reserve.totalDebt, reserve.totalVariableDebt, reserve.totalDebtUSD),
        borrowCap: tokenAmount(reserve.borrowCap, reserve.borrowCap, reserve.borrowCapUSD),
        reserveFactor: makePercentValue(reserve.reserveFactor),
        availableLiquidity: tokenAmount(
          reserve.formattedAvailableLiquidity,
          reserve.availableLiquidity,
          reserve.availableLiquidityUSD
        ),
        utilizationRate: makePercentValue(reserve.borrowUsageRatio),
        baseVariableBorrowRate: makePercentValue(reserve.baseVariableBorrowRate),
        variableRateSlope1: makePercentValue(reserve.variableRateSlope1),
        variableRateSlope2: makePercentValue(reserve.variableRateSlope2),
        optimalUsageRate: makePercentValue(reserve.optimalUsageRatio),
        borrowingState: isBorrowEnabled ? 'ENABLED' : 'DISABLED',
        borrowCapReached:
          reserve.borrowCap !== '0' && Number(reserve.totalDebt) >= Number(reserve.borrowCap),
      },
      isolationModeConfig: null,
      eModeInfo: [],
      incentives: [],
      userState: null,
    } as unknown as ReserveWithId;
  };

  const reserves = formattedPoolReserves.map(toReserve);
  const totalMarketSize = formattedPoolReserves.reduce(
    (sum, reserve) => sum + Number(reserve.totalLiquidityUSD || '0'),
    0
  );
  const totalAvailableLiquidity = formattedPoolReserves.reduce(
    (sum, reserve) => sum + Number(reserve.availableLiquidityUSD || '0'),
    0
  );

  return {
    ...marketInfo,
    __typename: 'Market' as const,
    totalMarketSize: totalMarketSize.toString(),
    totalAvailableLiquidity: totalAvailableLiquidity.toString(),
    supplyReserves: reserves,
    borrowReserves: reserves,
    eModeCategories: [],
    userState: null,
  } as unknown as Market;
};

/**
 * This is the only provider you'll ever need.
 * It fetches reserves /incentives & walletbalances & keeps them updated.
 */
export const AppDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { currentAccount } = useWeb3Context();

  const currentMarketData = useRootStore((state) => state.currentMarketData);
  const isDinolendMarket = currentMarketData.market === CustomMarket.dinolend_configured_v3;

  const { data, isPending } = useMarketsData({
    client,
    marketData: currentMarketData,
    account: currentAccount,
    enabled: !isDinolendMarket,
  });

  const marketAddress = currentMarketData.addresses.LENDING_POOL.toLowerCase();

  const sdkMarket = data?.find((item) => item.address.toLowerCase() === marketAddress);

  const { data: reservesData, isPending: reservesDataLoading } =
    usePoolReservesHumanized(currentMarketData);
  const { data: formattedPoolReserves, isPending: formattedPoolReservesLoading } =
    usePoolFormattedReserves(currentMarketData);
  const baseCurrencyData = reservesData?.baseCurrencyData;

  const dinolendMarket = isDinolendMarket
    ? buildDinolendMarket(currentMarketData, formattedPoolReserves)
    : undefined;
  const market = sdkMarket || dinolendMarket;

  const totalBorrows = market?.borrowReserves.reduce((acc, reserve) => {
    const value = reserve.borrowInfo?.total?.usd ?? 0;
    return acc + Number(value);
  }, 0);
  const supplyReserves = (market?.supplyReserves ?? []).map((reserve) => ({
    ...reserve,
    id: `${market?.address}-${reserve.underlyingToken.address}`,
  }));
  const borrowReserves = (market?.borrowReserves ?? []).map((reserve) => ({
    ...reserve,
    id: `${market?.address}-${reserve.underlyingToken.address}`,
  }));
  const eModeCategories = market?.eModeCategories ?? [];
  const marketUserState = market?.userState ?? undefined;
  // user hooks

  const eModes = formattedPoolReserves ? formatEmodes(formattedPoolReserves) : {};

  const { data: userReservesData, isPending: userReservesDataLoading } =
    useUserPoolReservesHumanized(currentMarketData);
  const { data: userSummary, isPending: userSummaryLoading } =
    useExtendedUserSummaryAndIncentives(currentMarketData);
  const userReserves = userReservesData?.userReserves;

  // loading
  const isReservesLoading = reservesDataLoading || formattedPoolReservesLoading;
  const isUserDataLoading = userReservesDataLoading || userSummaryLoading;

  const loading =
    (isDinolendMarket ? false : isPending) ||
    isReservesLoading ||
    (!!currentAccount && isUserDataLoading);

  return (
    <AppDataContext.Provider
      value={{
        loading,
        market,
        totalBorrows,
        supplyReserves,
        borrowReserves,
        eModeCategories,
        userState: marketUserState,
        // Legacy fields (to be removed once consumers migrate)
        reserves: formattedPoolReserves || [],
        eModes,
        user: userSummary,
        userReserves: userReserves || [],
        marketReferencePriceInUsd: baseCurrencyData?.marketReferenceCurrencyPriceInUsd || '0',
        marketReferenceCurrencyDecimals: baseCurrencyData?.marketReferenceCurrencyDecimals || 0,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppDataContext = () => useContext(AppDataContext);
